/**
 * DedicatedServerListPage — Pilotage infra multi-clients (admin).
 *
 * Route : /ops/dedicated-servers
 *
 * - Header gradient + 6 stats cards top (total / installing / active / failed / coût / revenu).
 * - Toolbar : search (name|IP|Hetzner), export CSV, bouton "+ nouveau" (TODO provision override).
 * - Filtres : status, plan, region, organization, created_at, hetzner_server_id.
 * - Table AntD pagination 50/page, 11 colonnes (cf spec §4.2).
 *
 * @spec specs/app/admin/dedicated-servers.md §4
 */

import { useTable } from '@refinedev/antd';
import { CanAccess, useCustom } from '@refinedev/core';
import { Button, DatePicker, Empty, Input, InputNumber, Result, Select, Table, Tag } from 'antd';
import type { CrudFilters } from '@refinedev/core';
import {
  AlertCircle,
  Copy,
  Download,
  Eye,
  Filter,
  RefreshCw,
  Search,
  Server as ServerIcon,
} from 'lucide-react';
import { useMemo, useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import ServerStatusBadge from './components/ServerStatusBadge';
import type {
  DedicatedServer,
  DedicatedServerStats,
  ServerPlan,
  ServerStatus,
} from '@/types/dedicatedServer';
import {
  REGION_LABELS,
  SERVER_PLAN_LABELS,
} from '@/types/dedicatedServer';

const STATUS_OPTIONS: Array<{ value: ServerStatus; label: string }> = [
  { value: 'provisioning', label: 'Provisioning' },
  { value: 'installing', label: 'Installation' },
  { value: 'active', label: 'Actif' },
  { value: 'failed', label: 'Échoué' },
  { value: 'suspended', label: 'Suspendu' },
  { value: 'destroyed', label: 'Détruit' },
];

const PLAN_OPTIONS: Array<{ value: ServerPlan; label: string }> = (
  Object.keys(SERVER_PLAN_LABELS) as ServerPlan[]
).map((p) => ({ value: p, label: SERVER_PLAN_LABELS[p] }));

const REGION_OPTIONS = Object.keys(REGION_LABELS).map((r) => ({
  value: r,
  label: `${REGION_LABELS[r]?.flag ?? ''} ${REGION_LABELS[r]?.label ?? r}`,
}));

const formatCurrency = (n: number): string =>
  `${n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;

const formatRelative = (iso: string): string => {
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  if (Number.isNaN(diff)) return '—';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days} j`;
};

const StatCard: FC<{
  label: string;
  value: string | number;
  icon: FC<{ className?: string }>;
  iconColor: string;
  pulse?: boolean;
  onClick?: () => void;
  dangerous?: boolean;
}> = ({ label, value, icon: Icon, iconColor, pulse, onClick, dangerous }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className={`fc-card flex items-center gap-3 p-4 text-left ${
      onClick ? 'cursor-pointer transition-shadow hover:shadow-md' : 'cursor-default'
    } ${dangerous ? 'border-red-300 dark:border-red-900' : ''}`}
  >
    <div
      className={`flex h-10 w-10 items-center justify-center rounded-lg ${iconColor} ${
        pulse ? 'animate-pulse' : ''
      }`}
    >
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <p className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p
        className={`text-lg font-bold ${
          dangerous ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-white'
        }`}
      >
        {value}
      </p>
    </div>
  </button>
);

const DedicatedServerListPage: FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ServerStatus[]>([]);
  const [planFilter, setPlanFilter] = useState<ServerPlan[]>([]);
  const [regionFilter, setRegionFilter] = useState<string[]>([]);
  const [orgIdFilter, setOrgIdFilter] = useState('');
  const [hetznerIdFilter, setHetznerIdFilter] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);

  // Build filters array for Refine useTable
  const activeFilters: CrudFilters = useMemo(() => {
    const filters: CrudFilters = [];
    if (searchQuery.trim()) {
      filters.push({ field: 'search', operator: 'contains', value: searchQuery.trim() });
    }
    if (statusFilter.length > 0) {
      filters.push({ field: 'status', operator: 'in', value: statusFilter });
    }
    if (planFilter.length > 0) {
      filters.push({ field: 'plan', operator: 'in', value: planFilter });
    }
    if (regionFilter.length > 0) {
      filters.push({ field: 'region', operator: 'in', value: regionFilter });
    }
    if (orgIdFilter.trim()) {
      filters.push({ field: 'organization_id', operator: 'eq', value: orgIdFilter.trim() });
    }
    if (hetznerIdFilter != null) {
      filters.push({ field: 'hetzner_server_id', operator: 'eq', value: hetznerIdFilter });
    }
    if (dateRange) {
      filters.push({ field: 'created_at_from', operator: 'gte', value: dateRange[0] });
      filters.push({ field: 'created_at_to', operator: 'lte', value: dateRange[1] });
    }
    return filters;
  }, [
    searchQuery,
    statusFilter,
    planFilter,
    regionFilter,
    orgIdFilter,
    hetznerIdFilter,
    dateRange,
  ]);

  const { tableProps, tableQueryResult } = useTable<DedicatedServer>({
    resource: 'admin/dedicated-servers',
    pagination: { pageSize: 50 },
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    filters: { permanent: activeFilters },
  });

  const { data: statsData } = useCustom<{ data: DedicatedServerStats } | DedicatedServerStats>({
    url: '/api/v1/admin/dedicated-servers/stats',
    method: 'get',
    queryOptions: { refetchInterval: 15_000, retry: 0 },
  });

  const stats = useMemo<DedicatedServerStats | undefined>(() => {
    const payload = statsData?.data as
      | { data: DedicatedServerStats }
      | DedicatedServerStats
      | undefined;
    if (!payload) return undefined;
    return 'total' in payload ? payload : (payload as { data: DedicatedServerStats }).data;
  }, [statsData]);

  const resetFilters = () => {
    setSearchQuery('');
    setStatusFilter([]);
    setPlanFilter([]);
    setRegionFilter([]);
    setOrgIdFilter('');
    setHetznerIdFilter(null);
    setDateRange(null);
  };

  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    statusFilter.length > 0 ||
    planFilter.length > 0 ||
    regionFilter.length > 0 ||
    orgIdFilter.trim() !== '' ||
    hetznerIdFilter != null ||
    dateRange != null;

  const handleExportCsv = () => {
    const rows = tableProps.dataSource ?? [];
    if (rows.length === 0) return;
    const headers = [
      'id',
      'name',
      'organization_name',
      'plan',
      'region',
      'status',
      'ip_address',
      'monthly_cost_eur',
      'created_at',
      'last_activity_at',
    ];
    const csv = [
      headers.join(','),
      ...rows.map((r) =>
        [
          r.id,
          r.name,
          r.organization_name ?? r.organization_id,
          r.plan,
          r.region,
          r.status,
          r.ip_address ?? '',
          r.monthly_cost_eur,
          r.created_at,
          r.last_activity_at,
        ]
          .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
          .join(','),
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dedicated-servers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (value: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(value);
    }
  };

  if (tableQueryResult.isError) {
    return (
      <CanAccess resource="admin/dedicated-servers" action="list" fallback={<AccessDeniedResult />}>
        <div className="space-y-6">
          <GradientHeader />
          <Result
            status="error"
            title="Impossible de charger les serveurs"
            subTitle="Une erreur est survenue côté backend. Vérifier l'endpoint GET /api/v1/admin/dedicated-servers."
            extra={
              <Button
                type="primary"
                icon={<RefreshCw className="h-4 w-4" />}
                onClick={() => void tableQueryResult.refetch()}
              >
                Réessayer
              </Button>
            }
          />
        </div>
      </CanAccess>
    );
  }

  return (
    <CanAccess resource="admin/dedicated-servers" action="list" fallback={<AccessDeniedResult />}>
      <div className="space-y-6">
        <GradientHeader />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <StatCard
            label="Total"
            value={stats?.total ?? '—'}
            icon={ServerIcon}
            iconColor="bg-blue-100 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300"
          />
          <StatCard
            label="Installing"
            value={stats?.installing ?? 0}
            icon={RefreshCw}
            iconColor="bg-cyan-100 text-cyan-600 dark:bg-cyan-950/40 dark:text-cyan-300"
            pulse={(stats?.installing ?? 0) > 0}
          />
          <StatCard
            label="Actifs"
            value={stats?.active ?? 0}
            icon={ServerIcon}
            iconColor="bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300"
          />
          <StatCard
            label="Failed"
            value={stats?.failed ?? 0}
            icon={AlertCircle}
            iconColor="bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-300"
            dangerous={(stats?.failed ?? 0) > 0}
            onClick={() => setStatusFilter(['failed'])}
          />
          <StatCard
            label="Coût infra"
            value={stats ? formatCurrency(stats.monthly_cost_eur_total) : '—'}
            icon={ServerIcon}
            iconColor="bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-300"
          />
          <StatCard
            label="Revenu"
            value={stats ? formatCurrency(stats.monthly_revenue_eur_total) : '—'}
            icon={ServerIcon}
            iconColor="bg-violet-100 text-violet-600 dark:bg-violet-950/40 dark:text-violet-300"
          />
        </div>

        <div className="fc-card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              className="max-w-sm flex-1"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par nom, IP, Hetzner name…"
              prefix={<Search className="h-4 w-4 text-slate-400" />}
              allowClear
            />
            <div className="flex flex-1 items-center gap-2" />
            <Button icon={<Download className="h-4 w-4" />} onClick={handleExportCsv}>
              Export CSV
            </Button>
            {hasActiveFilters && (
              <Button icon={<Filter className="h-4 w-4" />} onClick={resetFilters}>
                Réinitialiser
              </Button>
            )}
          </div>

          <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-6">
            <Select<ServerStatus[]>
              mode="multiple"
              allowClear
              placeholder="Status"
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              size="small"
            />
            <Select<ServerPlan[]>
              mode="multiple"
              allowClear
              placeholder="Plan"
              options={PLAN_OPTIONS}
              value={planFilter}
              onChange={setPlanFilter}
              size="small"
            />
            <Select<string[]>
              mode="multiple"
              allowClear
              placeholder="Région"
              options={REGION_OPTIONS}
              value={regionFilter}
              onChange={setRegionFilter}
              size="small"
            />
            <Input
              placeholder="Organization ID (UUID)"
              value={orgIdFilter}
              onChange={(e) => setOrgIdFilter(e.target.value)}
              size="small"
            />
            <InputNumber
              placeholder="Hetzner server ID"
              value={hetznerIdFilter}
              onChange={(v) => setHetznerIdFilter(v as number | null)}
              style={{ width: '100%' }}
              size="small"
            />
            <DatePicker.RangePicker
              size="small"
              placeholder={['Créé depuis', 'Créé jusqu\'à']}
              onChange={(_, dates) => {
                if (dates?.[0] && dates?.[1]) {
                  setDateRange([dates[0], dates[1]]);
                } else {
                  setDateRange(null);
                }
              }}
            />
          </div>
        </div>

        <div className="fc-card overflow-hidden p-0">
          <Table<DedicatedServer>
            {...tableProps}
            rowKey="id"
            scroll={{ x: 1400 }}
            locale={{
              emptyText: (
                <Empty
                  description={
                    hasActiveFilters
                      ? 'Aucun serveur ne correspond aux critères'
                      : 'Aucun serveur dédié provisionné à ce jour'
                  }
                />
              ),
            }}
            pagination={{
              ...(tableProps.pagination as object),
              pageSize: 50,
              showSizeChanger: false,
              showTotal: (total: number) => `${total} serveur${total > 1 ? 's' : ''}`,
            }}
          >
            <Table.Column<DedicatedServer>
              dataIndex="id"
              title="ID"
              width={110}
              render={(id: string) => (
                <div className="flex items-center gap-1">
                  <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
                    {id.slice(0, 8)}…
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(id);
                    }}
                    className="text-slate-400 hover:text-blue-500"
                    title="Copier ID complet"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                </div>
              )}
            />
            <Table.Column<DedicatedServer>
              dataIndex="name"
              title="Nom"
              width={200}
              sorter={(a, b) => a.name.localeCompare(b.name)}
              render={(name: string) => (
                <div className="inline-flex items-center gap-2">
                  <ServerIcon className="h-4 w-4 text-slate-400" />
                  <span className="font-medium text-slate-900 dark:text-white">{name}</span>
                </div>
              )}
            />
            <Table.Column<DedicatedServer>
              dataIndex="organization_name"
              title="Organisation"
              width={200}
              render={(name: string | undefined, r) => (
                <a
                  href={`/ops/organizations/${r.organization_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-blue-600 hover:underline dark:text-blue-400"
                >
                  {name ?? r.organization_id.slice(0, 8)}
                </a>
              )}
            />
            <Table.Column<DedicatedServer>
              dataIndex="plan"
              title="Plan"
              width={100}
              sorter={(a, b) => a.plan.localeCompare(b.plan)}
              render={(plan: ServerPlan) => (
                <Tag color="blue">{SERVER_PLAN_LABELS[plan] ?? plan}</Tag>
              )}
            />
            <Table.Column<DedicatedServer>
              dataIndex="region"
              title="Région"
              width={130}
              sorter={(a, b) => a.region.localeCompare(b.region)}
              render={(r: string) => {
                const cfg = REGION_LABELS[r];
                return (
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300">
                    {cfg?.flag ?? '🌍'} {cfg?.label ?? r}
                  </span>
                );
              }}
            />
            <Table.Column<DedicatedServer>
              dataIndex="status"
              title="Status"
              width={140}
              render={(status: ServerStatus) => <ServerStatusBadge status={status} size="small" />}
            />
            <Table.Column<DedicatedServer>
              dataIndex="ip_address"
              title="IP"
              width={150}
              render={(ip: string | null) =>
                ip ? (
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-xs text-slate-700 dark:text-slate-300">
                      {ip}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(ip);
                      }}
                      className="text-slate-400 hover:text-blue-500"
                      title="Copier IP"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <span className="text-slate-400">—</span>
                )
              }
            />
            <Table.Column<DedicatedServer>
              dataIndex="created_at"
              title="Créé"
              width={140}
              sorter={(a, b) => a.created_at.localeCompare(b.created_at)}
              defaultSortOrder="descend"
              render={(iso: string) => (
                <span className="text-xs text-slate-500 dark:text-slate-400" title={iso}>
                  {formatRelative(iso)}
                </span>
              )}
            />
            <Table.Column<DedicatedServer>
              dataIndex="last_activity_at"
              title="Activité"
              width={140}
              sorter={(a, b) => a.last_activity_at.localeCompare(b.last_activity_at)}
              render={(iso: string) => (
                <span className="text-xs text-slate-500 dark:text-slate-400" title={iso}>
                  {formatRelative(iso)}
                </span>
              )}
            />
            <Table.Column<DedicatedServer>
              dataIndex="monthly_cost_eur"
              title="Coût"
              width={100}
              align="right"
              sorter={(a, b) => a.monthly_cost_eur - b.monthly_cost_eur}
              render={(n: number) => (
                <span className="font-medium text-slate-900 dark:text-white">
                  {formatCurrency(n)}
                </span>
              )}
            />
            <Table.Column<DedicatedServer>
              title="Actions"
              width={80}
              fixed="right"
              render={(_: unknown, record) => (
                <Button
                  type="text"
                  size="small"
                  icon={<Eye className="h-4 w-4" />}
                  onClick={() => navigate(`/ops/dedicated-servers/${record.id}`)}
                >
                  Voir
                </Button>
              )}
            />
          </Table>
        </div>
      </div>
    </CanAccess>
  );
};

const GradientHeader: FC = () => (
  <div className="fc-gradient-header rounded-2xl p-6 text-white">
    <div className="flex items-center gap-3">
      <ServerIcon className="h-6 w-6" />
      <div>
        <h1 className="text-xl font-bold">Serveurs dédiés</h1>
        <p className="mt-1 text-sm text-blue-100">Pilotage infra multi-clients</p>
      </div>
    </div>
  </div>
);

const AccessDeniedResult: FC = () => (
  <Result
    status="403"
    title="403"
    subTitle="Accès refusé. Cette page est réservée aux administrateurs."
  />
);

export default DedicatedServerListPage;
